import { useState } from "react";

const PLATFORMS = {
  instagram: { label: "Instagram", color: "#C13584", bg: "#FCE4EC", text: "#880E4F" },
  linkedin: { label: "LinkedIn", color: "#0077B5", bg: "#E3F2FD", text: "#0D47A1" },
  facebook: { label: "Facebook", color: "#1877F2", bg: "#E8F0FE", text: "#1565C0" },
};

const STATUSES = {
  draft: { label: "Draft", bg: "#F3F4F6", text: "#374151" },
  scheduled: { label: "Scheduled", bg: "#FEF9C3", text: "#854D0E" },
  published: { label: "Published", bg: "#DCFCE7", text: "#166534" },
};

const getPlatform = (key) => PLATFORMS[key] || { label: key, color: "#888", bg: "#F3F4F6", text: "#374151" };
const getStatus = (key) => STATUSES[key] || STATUSES.draft;

const samplePosts = [
  { id: 1, content: "Exciting new product launch coming soon! Stay tuned for the big reveal.", platforms: ["instagram", "facebook"], scheduledAt: "2026-05-18T10:00", status: "scheduled", image: "" },
  { id: 2, content: "We are thrilled to announce our partnership with leading industry innovators.", platforms: ["linkedin"], scheduledAt: "2026-05-16T09:00", status: "published", image: "" },
  { id: 3, content: "Check out our weekend special offers — limited time only!", platforms: ["instagram", "facebook", "linkedin"], scheduledAt: "2026-05-20T14:00", status: "draft", image: "" },
];

async function postToInstagram(content, apiKey, imageUrl) {
  console.log("[Instagram API] Posting:", { content, apiKey: apiKey || "NOT_SET", imageUrl });
  return { success: true, platform: "instagram" };
}
async function postToLinkedIn(content, apiKey) {
  console.log("[LinkedIn API] Posting:", { content, apiKey: apiKey || "NOT_SET" });
  return { success: true, platform: "linkedin" };
}
async function postToFacebook(content, apiKey, imageUrl) {
  console.log("[Facebook API] Posting:", { content, apiKey: apiKey || "NOT_SET", imageUrl });
  return { success: true, platform: "facebook" };
}
async function publishPost(post, apiKeys) {
  return Promise.all(post.platforms.map(p => {
    if (p === "instagram") return postToInstagram(post.content, apiKeys.instagram, post.image);
    if (p === "linkedin") return postToLinkedIn(post.content, apiKeys.linkedin);
    if (p === "facebook") return postToFacebook(post.content, apiKeys.facebook, post.image);
    return Promise.resolve({ success: false });
  }));
}

const PlatformBadge = ({ p }) => {
  const pl = getPlatform(p);
  return <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 99, background: pl.bg, color: pl.text, marginRight: 4, display: "inline-block" }}>{pl.label}</span>;
};

const StatusBadge = ({ s }) => {
  const st = getStatus(s);
  return <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 99, background: st.bg, color: st.text }}>{st.label}</span>;
};

const emptyForm = { content: "", platforms: [], scheduledAt: "", status: "draft", image: "" };

export default function App() {
  const [tab, setTab] = useState("dashboard");
  const [posts, setPosts] = useState(samplePosts);
  const [apiKeys, setApiKeys] = useState({ instagram: "", linkedin: "", facebook: "" });
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [toast, setToast] = useState(null);
  const [editPost, setEditPost] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openComposer = (post = null) => {
    setEditPost(post);
    setForm(post ? { content: post.content, platforms: [...post.platforms], scheduledAt: post.scheduledAt, status: post.status, image: post.image } : emptyForm);
    setTab("composer");
  };

  const savePost = () => {
    if (!form.content.trim()) { showToast("Post content is required", "error"); return false; }
    if (!form.platforms.length) { showToast("Select at least one platform", "error"); return false; }
    if (editPost) {
      setPosts(prev => prev.map(p => p.id === editPost.id ? { ...editPost, ...form } : p));
      showToast("Post updated!");
    } else {
      setPosts(prev => [...prev, { id: Date.now(), ...form }]);
      showToast("Post saved!");
    }
    setEditPost(null);
    setForm(emptyForm);
    setTab("posts");
    return true;
  };

  const deletePost = (id) => { setPosts(prev => prev.filter(p => p.id !== id)); showToast("Post deleted"); };

  const handlePublish = async (post) => {
    const results = await publishPost(post, apiKeys);
    const allOk = results.every(r => r.success);
    setPosts(prev => prev.map(p => p.id === post.id ? { ...p, status: "published" } : p));
    showToast(allOk ? `Published to ${post.platforms.map(p => getPlatform(p).label).join(", ")}!` : "Some platforms failed — check console", allOk ? "success" : "error");
  };

  const togglePlatform = (p) => setForm(f => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p] }));

  const now = new Date("2026-05-15");
  const calDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(now); d.setDate(now.getDate() + i); return d; });
  const filtered = posts.filter(p => (filterPlatform === "all" || p.platforms.includes(filterPlatform)) && (filterStatus === "all" || p.status === filterStatus));

  const navItems = [
    { id: "dashboard", icon: "ti-layout-dashboard", label: "Dashboard" },
    { id: "composer", icon: "ti-pencil-plus", label: "Compose" },
    { id: "calendar", icon: "ti-calendar", label: "Calendar" },
    { id: "posts", icon: "ti-list", label: "All Posts" },
    { id: "settings", icon: "ti-settings", label: "Settings" },
  ];

  return (
    <div style={{ display: "flex", minHeight: 600, background: "var(--color-background-tertiary)", borderRadius: 12, overflow: "hidden", border: "0.5px solid var(--color-border-tertiary)", fontFamily: "system-ui, sans-serif", fontSize: 14, color: "var(--color-text-primary)" }}>
      {/* Sidebar */}
      <div style={{ width: 200, background: "var(--color-background-primary)", borderRight: "0.5px solid var(--color-border-tertiary)", display: "flex", flexDirection: "column", padding: "1.5rem 0" }}>
        <div style={{ padding: "0 1.25rem 1.5rem", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
          <div style={{ fontWeight: 500, fontSize: 15 }}>SocialHQ</div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Marketing Manager</div>
        </div>
        <nav style={{ padding: "1rem 0", flex: 1 }}>
          {navItems.map(n => (
            <button key={n.id} onClick={() => n.id === "composer" ? openComposer() : setTab(n.id)} style={{ width: "100%", textAlign: "left", padding: "0.6rem 1.25rem", display: "flex", alignItems: "center", gap: 10, background: tab === n.id ? "var(--color-background-secondary)" : "transparent", border: "none", cursor: "pointer", color: tab === n.id ? "var(--color-text-primary)" : "var(--color-text-secondary)", fontWeight: tab === n.id ? 500 : 400, fontSize: 14, borderLeft: tab === n.id ? "2px solid #0077B5" : "2px solid transparent" }}>
              <i className={`ti ${n.icon}`} style={{ fontSize: 17 }} aria-hidden="true" />{n.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "1rem 1.25rem", fontSize: 12, color: "var(--color-text-tertiary)", borderTop: "0.5px solid var(--color-border-tertiary)" }}>
          {Object.entries(PLATFORMS).map(([key, pl]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: apiKeys[key] ? "#16a34a" : "#d1d5db", display: "inline-block" }} />
              <span>{pl.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, overflow: "auto", padding: "1.5rem", position: "relative" }}>
        {toast && (
          <div style={{ position: "absolute", top: 16, right: 16, background: toast.type === "error" ? "#FEE2E2" : "#DCFCE7", color: toast.type === "error" ? "#991B1B" : "#166534", padding: "10px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 99, border: `0.5px solid ${toast.type === "error" ? "#FCA5A5" : "#86EFAC"}` }}>{toast.msg}</div>
        )}

        {/* Dashboard */}
        {tab === "dashboard" && (
          <div>
            <div style={{ marginBottom: "1.5rem" }}>
              <h2 style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>Dashboard</h2>
              <p style={{ margin: "4px 0 0", color: "var(--color-text-secondary)", fontSize: 13 }}>May 15, 2026</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: "1.5rem" }}>
              {[["scheduled","Scheduled"],["published","Published"],["draft","Drafts"]].map(([st, label]) => {
                const s = getStatus(st);
                return (
                  <div key={st} style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "1rem 1.25rem" }}>
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 26, fontWeight: 500 }}>{posts.filter(p => p.status === st).length}</div>
                    <div style={{ marginTop: 6 }}><StatusBadge s={st} /></div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
              <div style={{ fontWeight: 500, marginBottom: "1rem" }}>Upcoming posts</div>
              {posts.filter(p => p.status === "scheduled").length === 0 && <div style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>No scheduled posts yet.</div>}
              {posts.filter(p => p.status === "scheduled").map((p, i, arr) => (
                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < arr.length - 1 ? "0.5px solid var(--color-border-tertiary)" : "none" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>{p.content.slice(0, 70)}{p.content.length > 70 ? "…" : ""}</div>
                    <div>{p.platforms.map(pl => <PlatformBadge key={pl} p={pl} />)}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{new Date(p.scheduledAt).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                  <button onClick={() => handlePublish(p)} style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "transparent", cursor: "pointer", color: "var(--color-text-primary)" }}>Publish now</button>
                </div>
              ))}
            </div>
            <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "1rem 1.25rem" }}>
              <div style={{ fontWeight: 500, marginBottom: "1rem" }}>Platform overview</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                {Object.entries(PLATFORMS).map(([key, pl]) => (
                  <div key={key} style={{ background: pl.bg, borderRadius: 8, padding: "0.75rem 1rem" }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: pl.text }}>{pl.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 500, color: pl.text }}>{posts.filter(p => p.platforms.includes(key)).length}</div>
                    <div style={{ fontSize: 11, color: pl.text, opacity: 0.8 }}>total posts</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Composer */}
        {tab === "composer" && (
          <div>
            <div style={{ marginBottom: "1.5rem" }}>
              <h2 style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>{editPost ? "Edit post" : "New post"}</h2>
            </div>
            <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "1.25rem" }}>
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Platforms</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {Object.entries(PLATFORMS).map(([key, pl]) => (
                    <button key={key} onClick={() => togglePlatform(key)} style={{ padding: "6px 14px", borderRadius: 99, border: `1.5px solid ${form.platforms.includes(key) ? pl.color : "var(--color-border-tertiary)"}`, background: form.platforms.includes(key) ? pl.bg : "transparent", color: form.platforms.includes(key) ? pl.text : "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
                      {pl.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Post content</label>
                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={5} placeholder="Write your post here..." style={{ width: "100%", borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", padding: "10px 12px", fontSize: 14, resize: "vertical", background: "var(--color-background-secondary)", color: "var(--color-text-primary)", boxSizing: "border-box" }} />
                <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", textAlign: "right" }}>{form.content.length} chars</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: "1rem" }}>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Schedule date & time</label>
                  <input type="datetime-local" value={form.scheduledAt} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))} style={{ width: "100%", borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", padding: "8px 12px", fontSize: 13, background: "var(--color-background-secondary)", color: "var(--color-text-primary)", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={{ width: "100%", borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", padding: "8px 12px", fontSize: 13, background: "var(--color-background-secondary)", color: "var(--color-text-primary)", boxSizing: "border-box" }}>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: "1.25rem" }}>
                <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Image URL (optional)</label>
                <input type="url" value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))} placeholder="https://example.com/image.jpg" style={{ width: "100%", borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", padding: "8px 12px", fontSize: 13, background: "var(--color-background-secondary)", color: "var(--color-text-primary)", boxSizing: "border-box" }} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={savePost} style={{ padding: "8px 20px", borderRadius: 8, background: "#0077B5", color: "#fff", border: "none", cursor: "pointer", fontWeight: 500, fontSize: 14 }}>{editPost ? "Update post" : "Save post"}</button>
                {form.platforms.length > 0 && form.content.trim() && (
                  <button onClick={async () => { const post = { id: editPost ? editPost.id : Date.now(), ...form }; const ok = savePost(); if (ok !== false) await handlePublish(post); }} style={{ padding: "8px 20px", borderRadius: 8, background: "#166534", color: "#fff", border: "none", cursor: "pointer", fontWeight: 500, fontSize: 14 }}>Publish now</button>
                )}
                <button onClick={() => setTab("posts")} style={{ padding: "8px 20px", borderRadius: 8, background: "transparent", color: "var(--color-text-secondary)", border: "0.5px solid var(--color-border-secondary)", cursor: "pointer", fontSize: 14 }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Calendar */}
        {tab === "calendar" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
              <h2 style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>Calendar</h2>
              <button onClick={() => openComposer()} style={{ padding: "7px 14px", borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", background: "transparent", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6, color: "var(--color-text-primary)" }}>
                <i className="ti ti-plus" aria-hidden="true" /> New post
              </button>
            </div>
            <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                {calDays.map((d, i) => (
                  <div key={i} style={{ padding: "10px 8px", textAlign: "center", borderRight: i < 6 ? "0.5px solid var(--color-border-tertiary)" : "none" }}>
                    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 2 }}>{d.toLocaleDateString("en", { weekday: "short" })}</div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{d.getDate()}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", minHeight: 200 }}>
                {calDays.map((d, i) => {
                  const dayPosts = posts.filter(p => p.scheduledAt && new Date(p.scheduledAt).toDateString() === d.toDateString());
                  return (
                    <div key={i} style={{ padding: 8, borderRight: i < 6 ? "0.5px solid var(--color-border-tertiary)" : "none", minHeight: 120 }}>
                      {dayPosts.map(p => {
                        const pl = getPlatform(p.platforms[0]);
                        return (
                          <div key={p.id} onClick={() => openComposer(p)} style={{ background: pl.bg, borderRadius: 6, padding: "4px 6px", marginBottom: 4, cursor: "pointer", fontSize: 11 }}>
                            <div style={{ fontWeight: 500, color: pl.text, marginBottom: 2 }}>{new Date(p.scheduledAt).toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" })}</div>
                            <div style={{ color: pl.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{p.content.slice(0, 30)}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* All Posts */}
        {tab === "posts" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
              <h2 style={{ margin: 0, fontWeight: 500, fontSize: 18 }}>All posts</h2>
              <button onClick={() => openComposer()} style={{ padding: "7px 14px", borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", background: "transparent", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6, color: "var(--color-text-primary)" }}>
                <i className="ti ti-plus" aria-hidden="true" /> New post
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: "1rem" }}>
              <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} style={{ borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", padding: "6px 12px", fontSize: 13, background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
                <option value="all">All platforms</option>
                {Object.entries(PLATFORMS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", padding: "6px 12px", fontSize: 13, background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
                <option value="all">All statuses</option>
                {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, overflow: "hidden" }}>
              {filtered.length === 0 && <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-secondary)" }}>No posts found.</div>}
              {filtered.map((p, i) => (
                <div key={p.id} style={{ padding: "1rem 1.25rem", borderBottom: i < filtered.length - 1 ? "0.5px solid var(--color-border-tertiary)" : "none", display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, marginBottom: 6 }}>{p.content}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {p.platforms.map(pl => <PlatformBadge key={pl} p={pl} />)}
                      <StatusBadge s={p.status} />
                      {p.scheduledAt && <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{new Date(p.scheduledAt).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {p.status !== "published" && (
                      <button onClick={() => handlePublish(p)} title="Publish now" style={{ padding: "5px 10px", borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "transparent", cursor: "pointer", color: "#166534" }}>
                        <i className="ti ti-send" style={{ fontSize: 15 }} aria-hidden="true" />
                      </button>
                    )}
                    <button onClick={() => openComposer(p)} title="Edit" style={{ padding: "5px 10px", borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "transparent", cursor: "pointer", color: "var(--color-text-primary)" }}>
                      <i className="ti ti-edit" style={{ fontSize: 15 }} aria-hidden="true" />
                    </button>
                    <button onClick={() => deletePost(p.id)} title="Delete" style={{ padding: "5px 10px", borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "transparent", cursor: "pointer", color: "#991B1B" }}>
                      <i className="ti ti-trash" style={{ fontSize: 15 }} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings */}
        {tab === "settings" && (
          <div>
            <h2 style={{ margin: "0 0 1.5rem", fontWeight: 500, fontSize: 18 }}>Settings — API Keys</h2>
            <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "1.25rem", marginBottom: "1rem" }}>
              <p style={{ margin: "0 0 1rem", fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
                Enter your API keys below to enable real publishing. Keys are stored in app memory only.
              </p>
              {Object.entries(PLATFORMS).map(([key, pl]) => (
                <div key={key} style={{ marginBottom: "1rem" }}>
                  <label style={{ fontSize: 12, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, marginBottom: 6, color: pl.text }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: pl.color, display: "inline-block" }} />
                    {pl.label} API key
                  </label>
                  <input type="password" value={apiKeys[key]} onChange={e => setApiKeys(k => ({ ...k, [key]: e.target.value }))} placeholder={`Paste your ${pl.label} API key here`} style={{ width: "100%", borderRadius: 8, border: "0.5px solid var(--color-border-secondary)", padding: "8px 12px", fontSize: 13, background: "var(--color-background-secondary)", color: "var(--color-text-primary)", boxSizing: "border-box" }} />
                  <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 4 }}>
                    {key === "instagram" && "Instagram Graph API — requires Instagram Business account and Facebook Developer App"}
                    {key === "linkedin" && "LinkedIn API — requires LinkedIn Developer App with w_member_social permission"}
                    {key === "facebook" && "Facebook Graph API — requires Facebook Page Access Token with pages_manage_posts"}
                  </div>
                </div>
              ))}
              <button onClick={() => showToast("API keys saved!")} style={{ padding: "8px 20px", borderRadius: 8, background: "#0077B5", color: "#fff", border: "none", cursor: "pointer", fontWeight: 500, fontSize: 14 }}>Save keys</button>
            </div>
            <div style={{ background: "#FEF9C3", border: "0.5px solid #FDE68A", borderRadius: 10, padding: "1rem 1.25rem", fontSize: 13, color: "#854D0E" }}>
              <strong>Developer note:</strong> API hook functions are defined in source as <code>postToInstagram()</code>, <code>postToLinkedIn()</code>, and <code>postToFacebook()</code>. Replace the placeholder logic inside each with real HTTP calls to the respective platform APIs.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}